import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { authApi } from '@/lib/pillarApi';
import { normalizeUserEmailInputForSubmission } from '@anchorbase/pillar-sdk';

export default function ForgotPasswordPage() {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');

    const requestResetMutation = useMutation({
        mutationFn: (normalizedEmail: string) =>
            authApi.requestPasswordReset(normalizedEmail),
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const normalizedEmail: string = normalizeUserEmailInputForSubmission(email);
        setEmail(normalizedEmail);
        if (normalizedEmail.length === 0) {
            return;
        }
        requestResetMutation.mutate(normalizedEmail);
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <Card className="max-w-md w-full">
                <CardHeader>
                    <CardTitle className="text-center">Reset Password</CardTitle>
                    <CardDescription className="text-center">
                        Enter your email address and we'll send you a password reset link
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {requestResetMutation.isSuccess && (
                        <>
                            <Alert>
                                <AlertDescription className="text-center">
                                    <div className="text-green-600 font-semibold text-lg mb-2">
                                        Password Reset Link Generated
                                    </div>
                                    <p className="mb-2">{requestResetMutation.data.message}</p>
                                </AlertDescription>
                            </Alert>

                            <Button onClick={() => navigate('/login')} className="w-full">
                                Back to Login
                            </Button>
                        </>
                    )}

                    {!requestResetMutation.isSuccess && (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) =>
                                        setEmail(
                                            normalizeUserEmailInputForSubmission(
                                                e.target.value
                                            )
                                        )
                                    }
                                    onBlur={(e) =>
                                        setEmail(
                                            normalizeUserEmailInputForSubmission(
                                                e.currentTarget.value
                                            )
                                        )
                                    }
                                    placeholder="Enter your email address"
                                    disabled={requestResetMutation.isPending}
                                    required
                                />
                            </div>

                            {requestResetMutation.isError && (
                                <Alert variant="destructive">
                                    <AlertDescription>
                                        {requestResetMutation.error instanceof Error
                                            ? requestResetMutation.error.message
                                            : 'Failed to request password reset. Please try again.'}
                                    </AlertDescription>
                                </Alert>
                            )}

                            <Button
                                type="submit"
                                className="w-full"
                                disabled={requestResetMutation.isPending || !email}
                            >
                                {requestResetMutation.isPending ? 'Sending...' : 'Send Reset Link'}
                            </Button>

                            <div className="text-center">
                                <a href="/login" className="text-sm text-primary hover:underline">
                                    Back to Login
                                </a>
                            </div>
                        </form>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
